import clsx from 'clsx'
import update from 'immutability-helper'
import React, { useRef } from 'react'
import {useHistory, Link} from 'react-router-dom'
import {makeStyles} from 'external/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import create, {createPost} from 'helpers/command'
import CreateSection from './create-section'
import Section from './section'
import DialogContentText from '@mui/material/DialogContentText'
import Dialog from '../../responsive-fullscreen-dialog'

import SearchAndSelectQuestion from 'components/quiz/components/search-and-select-question'

import {initQuestion} from 'helpers/quiz'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    minWidth: 300,
    border: 'solid 1px #dfdfdf',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    padding: '15px 10px'
  },
  standardsLink: {
    color: '#1d9be5',
    fontSize: '27pt',
    padding: '1px 2px 1px 10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    "&:hover": {
      color:'#3977ba'
    },
    "&.selected": {
      color: '#01507e'
    }
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto'
  }
}))

export default function AssessmentSections ({context}) {

  const classes = useStyles()
  const history = useHistory()
  const {quizSections: sections, translate, updateState} = context
  const hasSections =  sections.length > 0
  const [deleteQuestionState, setDeleteQuestionState] = React.useState({question: undefined, opened: false})

  async function handleCreateSection() {
    const assessment = context.quizAssessment.id
    const [newSection] = await create('quiz', 'createSection')({assessment})
    const quizSections = update(context.quizSections, {$push: [newSection]})
    context.setState({quizSections})
  }

  function handleSectionMove (drag, hover) {
    const section = context.quizSections[drag]
    const quizSections = update(context.quizSections, {$splice: [[drag, 1], [hover, 0, section]]})
    context.setState({quizSections})
  }

  async function handleSectionDropped () {
    const assessment = context.quizAssessment.id
    const sections = context.quizSections
      .map((section, index) => ({id: section.id, index: section.index == index ? false : index}))
      .filter(section => section.index !== false)
    const movedSections = await createPost('quiz', 'reorderSections')({assessment, sections})
    const quizSections = movedSections.reduce((sections, section) => {
      const index = context.quizSections.findIndex(({id}) => section.id == id)
      return update(sections, {[index]: {index: {$set: section.index}}})
    }, context.quizSections)
    context.setState({quizSections})
  }

  async function handleQuestionMoved(drag, hover) {

    const question = update(context.quizQuestions[drag.section][drag.index], {section: {$set: hover.section}})
    const moveQuestion = hover.index == undefined
      ? {$set: [question]}
      : {$splice: [[hover.index, 0, question]]}
    const removeQuestion = {$splice: [[drag.index, 1]]}

    let quizQuestions = update(context.quizQuestions, {[drag.section]: removeQuestion})
    quizQuestions = update(quizQuestions, {[hover.section]: moveQuestion})
    context.setState({quizQuestions})
  }

  async function handleQuestionDropped() {

    const questions = Object.values(context.quizQuestions).reduce((acc, questions) => {
      return acc.concat(questions
        .map((question, index) => ({id: question.id, section: question.section, index: question.index == index ? false : index}))
        .filter(question => question.index !== false)
      )
    },[])

    if (questions.length > 0) {
      const movedQuestions = await createPost('quiz', 'reorderQuestions')({questions})
      const quizQuestions = movedQuestions.reduce((questions, question) => {
        const index = context.quizQuestions[question.section].findIndex(({id}) => question.question == id)
        return update(questions, {[question.section]: {[index]: {index: {$set: question.index}}}})
      }, context.quizQuestions)
      context.setState({quizQuestions})
    }
  }

  function handleToggleSection (id) {
    const index = context.quizSections.findIndex(section => section.id == id)
    const quizSections = update(context.quizSections, {[index]: {opened: {$apply: a => !a}}}) 
    context.setState({quizSections})
  }

  function handleOpenDeleteQuestion(question){
    setDeleteQuestionState({opened: true, question})
  }

  async function handleDeleteQuestion(confirmed) {
    if (confirmed) {
      const {id,section} = deleteQuestionState.question

      const questionIndex = context.quizQuestions[section].findIndex(q => q.id ==id)
      const goToQuestion = context.quizQuestions[section][questionIndex == 0 ? 1 : questionIndex - 1]

      const [deletedId] = await create('quiz','deleteQuestion')({id})
      if (deletedId == id) {
        const questions = questions => questions.filter(q => q.id != id).map((q,index) => update(q, {index: {$set: index}}))
        const quizQuestions = update(context.quizQuestions, {[section]: {$apply: questions}})
        context.setState({quizQuestions})
        history.push(context.link({},(goToQuestion ? `/:languageId/:role/:page/:assessment/edit/:sectionId/${goToQuestion.id}` : `/:languageId/:role/:page/:assessment/edit/${section}`)))
      }
    }
    setDeleteQuestionState({opened: false, question: undefined})
  }

  React.useEffect(() => {

    const targets = Object.entries(context.quizQuestions).reduce((targets, [section, questions]) => {
      return targets.concat(
        questions
        .map((q,index) => (q.id != context.questionId && q.isNew && {section, index}))
        .filter(q => q)
    )}, [])

    if (targets.length > 0) {
      const updates = targets.reduce((acc, q) => {
          acc[q.section] = acc[q.section] || {}
          acc[q.section][q.index] = {isNew: {$set: false}}
          return acc
        }, {})

      context.updateState({quizQuestions: updates})
    }

  }, [context.questionId])

  async function handleSelectQuestion(question, section) {
    if (!question || !section) return
    const [data] = await create('quiz', 'addQuestionFromBank')({question, section})
    if (data) {
      const init = initQuestion(context)
      const command = context.quizQuestions[section] ? '$push' : '$set'
      updateState({quizQuestions: {
        [section]: {[command]: [init({...data, isNew: {$set: true}})]}
      }})
      history.push(context.link('quiz', context.assessment, 'edit', section, data.id))
    }
  }

  return (
      <Paper className={classes.root}>
        <SearchAndSelectQuestion onSelect={handleSelectQuestion} />
        <Box className={classes.header}>
          <CreateSection
            caption={translate('create-section')}
            onClick={handleCreateSection}
          />
        </Box>
        {/* <Fade in={hasSections}> */}
          <Box className={classes.sections}>
            {sections.map((section, index) => (
              <Section
                index={index}
                key={section.id}
                context={context}
                onSectionMove={handleSectionMove}
                onSectionDropped={handleSectionDropped}
                onDeleteQuestion={handleOpenDeleteQuestion}
                onQuestionMove={handleQuestionMoved}
                onQuestionDropped={handleQuestionDropped}
                onToggleSection={handleToggleSection}
                section={section}
                questions={context.quizQuestions[section.id]}
                translate={translate}
              />))}
          </Box>
        {/* </Fade> */}
        <Dialog
          open={deleteQuestionState.opened}
          close={handleDeleteQuestion}
          confirm={<Button variant="contained" color="primary" onClick={() => handleDeleteQuestion(true)}>{translate('delete')}</Button>}
          cancel={<Button variant="contained" color="secondary" onClick={() => handleDeleteQuestion()}>{translate('cancel')}</Button>}
          title={translate('confirm_delete_item')}
        >
          <DialogContentText>
            {translate('delete-question-message', deleteQuestionState.question)}
          </DialogContentText>
        </Dialog>
      </Paper>
  )
}
